/**
 * WebTrader App
 * Main routing component for the client-facing trading platform
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { TraderThemeProvider } from './context/TraderThemeContext';
import { TraderLanguageProvider } from './context/TraderLanguageContext';
import { TraderAuthProvider, useTraderAuth } from './context/TraderAuthContext';
import {TraderNotificationProvider} from './notifications/NotificationContext';
import { WebSocketProvider } from '../context/WebSocketContext';
import { Toaster } from 'sonner';
import './styles/trader.css';

// Pages
import TraderLogin from './pages/TraderLogin';
import TraderRegister from './pages/TraderRegister';
import TraderDashboard from './pages/TraderDashboard';
import TraderDeposit from './pages/TraderDeposit';
import TraderWithdraw from './pages/TraderWithdraw';
import TraderSettings from './pages/TraderSettings';
import TraderDocuments from './pages/TraderDocuments';
import TraderFunds from './pages/TraderFunds';
import TraderEconomicCalendar from './pages/TraderEconomicCalendar';
import TraderHelpCenter from './pages/TraderHelpCenter';

// Layout component
import TraderLayout from './components/TraderLayout';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useTraderAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1421] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/trader/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useTraderAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1421] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/trader" replace />;
  }
  
  return children;
};

// Main App with routes
const TraderRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <TraderLogin />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <TraderRegister />
        </PublicRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <TraderDashboard />
        </ProtectedRoute>
      } />
      <Route path="/funds" element={
        <ProtectedRoute>
          <TraderLayout>
            <TraderFunds />
          </TraderLayout>
        </ProtectedRoute>
      } />
      <Route path="/deposit" element={
        <ProtectedRoute>
          <TraderDeposit />
        </ProtectedRoute>
      } />
      <Route path="/withdraw" element={
        <ProtectedRoute>
          <TraderWithdraw />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <TraderLayout>
            <TraderEconomicCalendar />
          </TraderLayout>
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <TraderLayout>
            <TraderHelpCenter />
          </TraderLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <TraderSettings />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <TraderDocuments />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to dashboard or login */}
      <Route path="*" element={<Navigate to="/trader" replace />} />
    </Routes>
  );
};

// Wrapped App with all providers
const TraderSocketBoundary = ({children}) => {
  const {user}=useTraderAuth();
  return <WebSocketProvider key={user?.id || 'anonymous'} tokenKey="trader_token" enabled={!!user}>{children}</WebSocketProvider>;
};

const TraderApp = () => {
  return (
    <TraderThemeProvider>
      <TraderLanguageProvider>
        <TraderAuthProvider>
          <TraderNotificationProvider>
          <TraderSocketBoundary>
            <Toaster 
              position="top-right" 
              richColors 
              theme="dark"
              toastOptions={{
                style: {
                  background: '#1a2332',
                  border: '1px solid #374151',
                },
              }}
            />
            <TraderRoutes />
          </TraderSocketBoundary>
          </TraderNotificationProvider>
        </TraderAuthProvider>
      </TraderLanguageProvider>
    </TraderThemeProvider>
  );
};

export default TraderApp;
